/*eslint max-lines-per-function: ["error", 180]*/
/*eslint complexity: ["error", 25]*/
import React, { useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import type { Value } from '@wojtekmaj/react-daterange-picker/dist/shared/types';

import { NewDatePicker } from '../../../app/components/NewDatePicker';
import { NewDateRangePicker } from '../../../app/components/NewDateRangePicker';
import momencik from '../../../common/momencik';
import CompanyByNameOrNipSelector from '../Company/CompanyByNameOrNipSelector';

import type { DateRangeState } from './dateRangeUtils';

export type DateRangeFilterApplied = {
  dateFrom: string;
  dateTo: string;
  companyId?: string;
  companyLabel?: string;
};

type SuperAdminDateRangeFilterProps = {
  filters: DateRangeState;
  setFilters: React.Dispatch<React.SetStateAction<DateRangeState>>;
  onApply: (applied: DateRangeFilterApplied) => void;
  onClear?: () => void;
  applyLabel?: string;
  extraFilter?: React.ReactNode;
  withCompany?: boolean;
  monthPicker?: boolean;
};

export const SuperAdminDateRangeFilter: React.FC<
  SuperAdminDateRangeFilterProps
> = ({
  filters,
  setFilters,
  onApply,
  onClear,
  applyLabel = 'Pokaż zestawienie',
  extraFilter,
  withCompany,
  monthPicker,
}) => {
  const [companyId, setCompanyId] = useState<string | undefined>();
  const [companyLabel, setCompanyLabel] = useState<string | undefined>();
  const [appliedRange, setAppliedRange] = useState<
    DateRangeFilterApplied | undefined
  >();
  const [monthRange, setMonthRange] = useState<Value>(null);

  const hasCompany = !!withCompany;
  const hasExtraFilter = !!extraFilter;
  const hasEither = hasCompany || hasExtraFilter;
  const hasBoth = hasCompany && hasExtraFilter;
  const hasDateRange =
    !!appliedRange && (appliedRange.dateFrom || appliedRange.dateTo);

  const handleMonthRangeChange = (value: Value) => {
    setMonthRange(value);
    if (!value || !Array.isArray(value)) {
      setFilters(prev => ({ ...prev, dateFrom: '', dateTo: '' }));
      return;
    }
    const [start, end] = value;
    const dateFrom = start ? momencik(start as Date, 'YYYY-MM-DD') : '';
    const dateTo = end
      ? momencik(
          new Date(
            (end as Date).getFullYear(),
            (end as Date).getMonth() + 1,
            0,
          ),
          'YYYY-MM-DD',
        )
      : '';
    setFilters(prev => ({ ...prev, dateFrom, dateTo }));
  };

  const handleApply = () => {
    const applied: DateRangeFilterApplied = {
      ...filters,
      companyId,
      companyLabel,
    };
    setAppliedRange(applied);
    onApply(applied);
  };

  const handleClear = () => {
    setCompanyId(undefined);
    setCompanyLabel(undefined);
    onClear?.();
  };

  const dateColWidth = monthPicker
    ? hasBoth
      ? 4
      : hasEither
        ? 6
        : 8
    : hasBoth
      ? 2
      : hasEither
        ? 3
        : 4;

  return (
    <div className="border rounded-3 p-3 mb-4 bg-light">
      <Row className="g-3 align-items-end">
        {monthPicker ? (
          <Col xs={12} md={dateColWidth}>
            <Form.Group controlId="superadminMonthRange">
              <Form.Label>Zakres miesięcy</Form.Label>
              <NewDateRangePicker
                value={monthRange}
                onChange={handleMonthRangeChange}
              />
            </Form.Group>
          </Col>
        ) : (
          <>
            <Col xs={12} md={dateColWidth}>
              <Form.Group controlId="superadminDateFrom">
                <Form.Label>Data od</Form.Label>
                <NewDatePicker
                  value={
                    filters.dateFrom
                      ? new Date(`${filters.dateFrom}T00:00:00`)
                      : undefined
                  }
                  dateChanged={date =>
                    setFilters(prev => ({
                      ...prev,
                      dateFrom: momencik(date, 'YYYY-MM-DD'),
                    }))
                  }
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={dateColWidth}>
              <Form.Group controlId="superadminDateTo">
                <Form.Label>Data do</Form.Label>
                <NewDatePicker
                  value={
                    filters.dateTo
                      ? new Date(`${filters.dateTo}T00:00:00`)
                      : undefined
                  }
                  dateChanged={date =>
                    setFilters(prev => ({
                      ...prev,
                      dateTo: momencik(date, 'YYYY-MM-DD'),
                    }))
                  }
                />
              </Form.Group>
            </Col>
          </>
        )}
        {hasCompany && (
          <Col xs={12} md={hasBoth ? 3 : 4}>
            <Form.Group controlId="superadminCompany">
              <Form.Label>Firma</Form.Label>
              <CompanyByNameOrNipSelector
                value={companyId}
                onChange={option => {
                  setCompanyId(option?.value || undefined);
                  setCompanyLabel(option?.label || undefined);
                }}
                clearable={true}
              />
            </Form.Group>
          </Col>
        )}
        {hasExtraFilter && (
          <Col xs={12} md={hasBoth ? 3 : 4}>
            {extraFilter}
          </Col>
        )}
        <Col xs={12} md={hasEither ? 2 : 4} className="d-flex gap-2">
          <Button variant="primary" onClick={handleApply}>
            {applyLabel}
          </Button>
          {onClear && (
            <Button variant="outline-secondary" onClick={handleClear}>
              Wyczyść zakres
            </Button>
          )}
        </Col>
      </Row>
      {appliedRange && (
        <div className="text-muted mt-3 small">
          Zakres raportu:{' '}
          {hasDateRange
            ? `${appliedRange.dateFrom || '...'} - ${appliedRange.dateTo || '...'}`
            : 'cały okres (bez ograniczenia datą)'}
          {appliedRange.companyLabel && `, firma: ${appliedRange.companyLabel}`}
        </div>
      )}
    </div>
  );
};
